import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText, AlertTriangle } from 'lucide-react';

const LEGAL_CONTENT = {
  terms: {
    title: 'Terms & Conditions',
    icon: FileText,
    sections: [
      {
        heading: '1. Services',
        content:
          'Placement Point provides career services including CV editing and optimization, tailored cover letter writing, and strategic job application services. We apply for jobs on your behalf based on your profile, career goals, and target markets.',
      },
      {
        heading: '2. No Job Guarantee',
        content:
          'We do NOT guarantee job placement. We provide the tools, strategy, and dedicated effort; the final hiring decision rests solely with the employer or hiring company. Our commitment is to the quality of our process and the number of applications submitted.',
      },
      {
        heading: '3. Service Duration',
        content:
          'All packages run for a period of 4 to 8 weeks, covering the initial CV audit, the application phase, and the interview response cycle. The exact duration depends on your selected package.',
      },
      {
        heading: '4. Client Capacity',
        content:
          'To maintain service quality, we work with a maximum of 5–7 clients simultaneously. Availability is on a first-come, first-served basis.',
      },
      {
        heading: '5. Payment Terms',
        content:
          'Basic and Standard packages require one-time full payment before services begin. Premium packages offer a 70/30 installment option, where 70% is due upfront and 30% is due within 14 days of service commencement.',
      },
      {
        heading: '6. Client Obligations',
        content:
          'Clients must provide accurate information, respond to communications within 48 hours, and attend scheduled sessions. Failure to cooperate may impact the quality and timeline of service delivery.',
      },
      {
        heading: '7. Intellectual Property',
        content:
          'All CV documents, cover letters, and materials created during the service belong to the client upon full payment. Placement Point retains the right to use anonymized case studies for marketing purposes.',
      },
      {
        heading: '8. Dispute Resolution',
        content:
          'Any disputes arising from these terms shall be resolved through mediation. If mediation fails, the matter shall be subject to the jurisdiction of Nigerian courts.',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    icon: Shield,
    sections: [
      {
        heading: '1. Information We Collect',
        content:
          'We collect personal information you provide, including your name, email address, phone number, CV/resume content, career history, and payment details. This information is essential for delivering our career services.',
      },
      {
        heading: '2. How We Use Your Information',
        content:
          'Your information is used to: deliver career services (CV editing, job applications), communicate with you about your service progress, process payments securely, and send appointment reminders and updates.',
      },
      {
        heading: '3. Data Protection',
        content:
          'We implement industry-standard security measures to protect your personal data. Your CV and career information are stored securely and are only accessed by authorized team members working on your case.',
      },
      {
        heading: '4. Third-Party Sharing',
        content:
          'We do not sell or rent your personal information. We may share data with: payment processors (Flutterwave) for transaction processing, email service providers for communication, and potential employers only as part of your job application process.',
      },
      {
        heading: '5. Data Retention',
        content:
          'We retain your personal data for 12 months after service completion. You may request deletion of your data at any time by contacting us via email.',
      },
      {
        heading: '6. Equal Employment Opportunity (EEO) Data',
        content:
          'As part of the job application process, we may collect voluntary demographic information including gender, race/ethnicity, veteran status, and disability status. This information is provided on a strictly voluntary basis and is used to complete job applications on your behalf. You may decline to provide any or all of this information without affecting your eligibility for our services.',
      },
      {
        heading: '7. Your Rights',
        content:
          'You have the right to: access your personal data, request correction of inaccurate data, request deletion of your data, and withdraw consent at any time. Contact us at placementpoint.job@gmail.com for any data-related requests.',
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    icon: AlertTriangle,
    sections: [
      {
        heading: '1. Refund Window',
        content:
          'Refunds are only available within 24 hours of payment AND before any document review or work has begun on your case. Once our team has started reviewing your CV or other documents, no refund will be issued.',
      },
      {
        heading: '2. Service Extension',
        content:
          'If you are not satisfied with the results within the initial service period, we offer a complimentary 2-week service extension at no additional cost. This is our commitment to helping you achieve the best possible outcome.',
      },
      {
        heading: '3. No Refunds After Work Begins',
        content:
          'Due to the immediate nature of our services (CV review, cover letter creation, job applications), we do not offer refunds once work has commenced. The value is delivered from day one of service.',
      },
      {
        heading: '4. How to Request a Refund',
        content:
          'To request a refund within the 24-hour window, send an email to placementpoint.job@gmail.com with your payment reference and reason for the request. Refunds are processed within 3–5 business days.',
      },
      {
        heading: '5. Cancellation',
        content:
          'You may cancel your service at any time. However, cancellation does not entitle you to a refund if work has already begun. Any unused portion of the service is forfeited upon cancellation.',
      },
    ],
  },
};

const LegalModal = ({ isOpen, onClose, type = 'terms' }) => {
  const content = LEGAL_CONTENT[type];

  if (!isOpen || !content) return null;

  const Icon = content.icon;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-t-3xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold">{content.title}</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            <div className="prose prose-slate max-w-none">
              <p className="text-sm text-slate-500 mb-6">
                Last updated: {new Date().toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              {content.sections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h4 className="text-lg font-bold text-slate-900 mb-2">
                    {section.heading}
                  </h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {section.content}
                  </p>
                </div>
              ))}

              <div className="mt-8 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <p className="text-sm text-indigo-700 font-medium">
                  By using our services, you acknowledge that you have read,
                  understood, and agree to be bound by these{' '}
                  {content.title.toLowerCase()}.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-100 bg-slate-50 rounded-b-3xl">
            <button
              onClick={onClose}
              className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors"
            >
              I Understand
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LegalModal;
